import { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import Layout from '../components/layout/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { Search } from 'lucide-react';

const FAQPage = () => {
  const { t } = useLanguage();
  const [searchQuery, setSearchQuery] = useState('');

  const faqCategories = [
    {
      title: 'Über WEDVAYA',
      questions: [
        {
          q: 'Was ist WEDVAYA?',
          a: 'WEDVAYA ist ein globaler Marktplatz, der Brautpaare mit Reisenden verbindet, die authentische Hochzeitserlebnisse suchen. Wir ermöglichen es Paaren, Gäste aus der ganzen Welt zu ihrer Hochzeit einzuladen und so ihre Kosten zu decken, während Reisende die einzigartige Möglichkeit bekommen, an echten kulturellen Feierlichkeiten teilzunehmen.',
        },
        {
          q: 'Wie ist die Idee zu WEDVAYA entstanden?',
          a: 'Die Idee entstand aus der Erkenntnis, dass Hochzeiten weltweit die schönsten kulturellen Feiern sind – aber oft nur der engste Kreis eingeladen wird. Gleichzeitig suchen immer mehr Reisende nach authentischen Erlebnissen jenseits typischer Touristenattraktionen. WEDVAYA bringt diese beiden Bedürfnisse zusammen.',
        },
        {
          q: 'In welchen Ländern ist WEDVAYA verfügbar?',
          a: 'WEDVAYA ist weltweit verfügbar. Wir haben Hochzeiten in über 25 Ländern, darunter Deutschland, Österreich, Schweiz, Frankreich, Spanien, Italien, Indien, Japan und viele mehr. Die Plattform unterstützt 6 Sprachen und 7 Währungen.',
        },
      ],
    },
    {
      title: 'Für Gäste',
      questions: [
        {
          q: 'Warum sollte ich die Hochzeit eines Fremden besuchen?',
          a: 'Eine Hochzeit zu besuchen ist eine einzigartige Möglichkeit, in eine Kultur einzutauchen. Sie erleben Traditionen, Musik, Tanz, Essen und die Gastfreundschaft der Einheimischen hautnah. Es ist kein touristisches Programm – es ist das echte Leben. Viele unserer Gäste bezeichnen es als das unvergesslichste Erlebnis ihrer Reise.',
        },
        {
          q: 'Warum muss ich einen Beitrag zahlen?',
          a: 'Der Beitrag ist ein Zeichen der Wertschätzung gegenüber dem Brautpaar, das Sie in ihren intimsten Moment einlädt. Er hilft, die Kosten der Feier zu decken und stellt sicher, dass nur ernsthaft interessierte Gäste teilnehmen. Der Betrag variiert je nach Hochzeit und beinhaltet in der Regel Essen und Getränke.',
        },
        {
          q: 'Wie läuft der Buchungsprozess ab?',
          a: '1) Wählen Sie eine Hochzeit, die zu Ihren Reiseplänen passt. 2) Senden Sie eine Bewerbung mit einer kurzen Vorstellung. 3) Warten Sie auf die Genehmigung durch das Brautpaar (meist innerhalb von 48 Stunden). 4) Nach Genehmigung zahlen Sie sicher online über Stripe. 5) Sie erhalten alle Details und können direkt mit dem Brautpaar kommunizieren.',
        },
        {
          q: 'Was ist im Preis inbegriffen?',
          a: 'Der Preis beinhaltet typischerweise den Eintritt zu allen Zeremonien und Aktivitäten am jeweiligen Tag sowie Mahlzeiten. Transport und Unterkunft sind nicht inbegriffen. Genaue Details finden Sie in jeder Hochzeitsausschreibung.',
        },
        {
          q: 'Muss ich traditionelle Kleidung tragen?',
          a: 'Das hängt von der jeweiligen Hochzeit ab. Wir empfehlen, die Kleiderordnung zu respektieren, die in der Hochzeitsausschreibung angegeben ist. Viele Brautpaare freuen sich, wenn Gäste traditionelle Kleidung tragen – dies zeigt Respekt und Interesse an der Kultur. Der Kontaktperson kann oft bei der Beschaffung helfen.',
        },
        {
          q: 'Soll ich ein Geschenk mitbringen?',
          a: 'Ein Geschenk ist nicht verpflichtend, da Ihr Beitrag bereits ein Zeichen der Wertschätzung ist. Wenn Sie trotzdem etwas schenken möchten, ist ein kleines Souvenir aus Ihrem Heimatland oder Blumen eine nette Geste.',
        },
      ],
    },
    {
      title: 'Für Gastgeber (Brautpaare)',
      questions: [
        {
          q: 'Warum sollte ich Fremde zu meiner Hochzeit einladen?',
          a: 'Es gibt viele Gründe: 1) Sie können einen Teil Ihrer Hochzeitskosten decken. 2) Sie teilen Ihre Kultur und Traditionen mit Menschen aus aller Welt. 3) Es macht Ihre Hochzeit zu etwas ganz Besonderem und schafft interkulturelle Verbindungen. 4) Viele Paare berichten, dass die internationalen Gäste eine wunderbare Bereicherung waren.',
        },
        {
          q: 'Wie viel kostet es, Gastgeber zu werden?',
          a: 'Die Registrierung und das Einstellen Ihrer Hochzeit ist komplett kostenlos. WEDVAYA erhebt eine Vermittlungsgebühr von 15% auf jede erfolgreiche Buchung. Sie erhalten also 85% des Beitrags, den Gäste zahlen.',
        },
        {
          q: 'Wann erhalte ich mein Geld?',
          a: 'Die Auszahlung erfolgt automatisch 48 Stunden nach dem Hochzeitsdatum auf das von Ihnen angegebene Bankkonto. Diese Verzögerung dient dem Schutz aller Beteiligten.',
        },
        {
          q: 'Wie stelle ich sicher, dass die Gäste meine Hochzeit respektieren?',
          a: 'Alle Gäste zahlen im Voraus, was sicherstellt, dass sie ernsthaft interessiert sind. Sie können Bewerbungen prüfen und nur Gäste annehmen, die zu Ihrer Feier passen. Außerdem ermöglicht die Plattform direkten Kontakt vor der Hochzeit, sodass Sie die Gäste kennenlernen können.',
        },
        {
          q: 'Kann ich entscheiden, wie viele Gäste kommen?',
          a: 'Ja, Sie legen selbst fest, wie viele Plätze Sie anbieten möchten. Sie haben auch die volle Kontrolle darüber, welche Bewerbungen Sie annehmen oder ablehnen.',
        },
      ],
    },
    {
      title: 'Zahlungen & Stornierung',
      questions: [
        {
          q: 'Welche Zahlungsmethoden werden akzeptiert?',
          a: 'Wir akzeptieren alle gängigen Kredit- und Debitkarten (Visa, Mastercard, American Express) sowie weitere lokale Zahlungsmethoden über unseren Partner Stripe. Barzahlung ist nicht möglich.',
        },
        {
          q: 'Ist meine Zahlung sicher?',
          a: 'Ja, alle Zahlungen werden über Stripe abgewickelt, einen der weltweit führenden Zahlungsdienstleister. Ihre Zahlungsdaten werden verschlüsselt übertragen und niemals auf unseren Servern gespeichert.',
        },
        {
          q: 'Was passiert, wenn die Hochzeit abgesagt wird?',
          a: 'Im unwahrscheinlichen Fall einer Absage durch das Brautpaar erhalten Sie eine vollständige Rückerstattung Ihres Beitrags.',
        },
        {
          q: 'Was passiert, wenn ich nicht teilnehmen kann?',
          a: 'Bei Stornierung bis 30 Tage vor dem Event erhalten Sie eine volle Rückerstattung. Bei Stornierung innerhalb von 30 Tagen werden 50% des Beitrags einbehalten. Bei Nichterscheinen ohne Benachrichtigung wird der volle Betrag einbehalten.',
        },
      ],
    },
    {
      title: 'Sicherheit & Vertrauen',
      questions: [
        {
          q: 'Wie wird meine Privatsphäre geschützt?',
          a: 'WEDVAYA ist vollständig DSGVO-konform. Ihre persönlichen Daten werden verschlüsselt gespeichert und niemals an Dritte verkauft. Kontaktdaten zwischen Gästen und Gastgebern werden erst nach erfolgreicher Buchung ausgetauscht.',
        },
        {
          q: 'Was ist das Verifizierungsbadge?',
          a: 'Das Verifizierungsbadge ist ein optionales Feature, das Ihre Identität bestätigt und anderen Nutzern zusätzliches Vertrauen gibt. Es kostet eine einmalige Gebühr von 9,99€ und beinhaltet eine Identitätsprüfung.',
        },
        {
          q: 'Wie kann ich verdächtige Aktivitäten melden?',
          a: 'Wenn Sie verdächtige Aktivitäten bemerken, kontaktieren Sie bitte sofort unser Support-Team unter support@wedvaya.com. Wir nehmen alle Meldungen ernst und prüfen sie umgehend.',
        },
      ],
    },
  ];

  const filteredCategories = faqCategories.map(category => ({
    ...category,
    questions: category.questions.filter(
      q => 
        q.q.toLowerCase().includes(searchQuery.toLowerCase()) ||
        q.a.toLowerCase().includes(searchQuery.toLowerCase())
    ),
  })).filter(category => category.questions.length > 0);

  return (
    <Layout>
      <div className="min-h-screen bg-secondary" data-testid="faq-page">
        {/* Hero */}
        <section className="bg-primary py-16">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <h1 className="font-serif text-4xl md:text-5xl font-bold text-white mb-4">
              Häufig gestellte Fragen
            </h1>
            <p className="text-white/90 text-lg mb-8">
              Finden Sie Antworten auf die wichtigsten Fragen rund um WEDVAYA
            </p>
            
            {/* Search */}
            <div className="relative max-w-xl mx-auto">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Suchen Sie nach Antworten..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-12 py-6 text-lg rounded-full bg-white"
                data-testid="faq-search"
              />
            </div>
          </div>
        </section>

        {/* FAQ Content */}
        <section className="py-16">
          <div className="max-w-4xl mx-auto px-4">
            {filteredCategories.length === 0 ? (
              <Card className="p-12 text-center">
                <p className="text-muted-foreground text-lg">
                  Keine Ergebnisse gefunden. Versuchen Sie einen anderen Suchbegriff.
                </p>
              </Card>
            ) : (
              filteredCategories.map((category, categoryIndex) => (
                <div key={categoryIndex} className="mb-12">
                  <h2 className="font-serif text-2xl font-bold mb-6">
                    {category.title}
                  </h2>
                  <Card>
                    <CardContent className="p-0">
                      <Accordion type="single" collapsible>
                        {category.questions.map((item, index) => (
                          <AccordionItem 
                            key={index} 
                            value={`${categoryIndex}-${index}`}
                            className="border-b last:border-b-0"
                          >
                            <AccordionTrigger className="px-6 py-4 text-left hover:no-underline hover:bg-secondary/50">
                              <span className="font-medium">{item.q}</span>
                            </AccordionTrigger>
                            <AccordionContent className="px-6 pb-4 text-muted-foreground leading-relaxed">
                              {item.a}
                            </AccordionContent>
                          </AccordionItem>
                        ))}
                      </Accordion>
                    </CardContent>
                  </Card>
                </div>
              ))
            )}
          </div>
        </section>

        {/* Contact CTA */}
        <section className="py-12 bg-white border-t border-border">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <h3 className="font-serif text-2xl font-bold mb-4">
              Noch Fragen?
            </h3>
            <p className="text-muted-foreground mb-6">
              Unser Support-Team hilft Ihnen gerne weiter.
            </p>
            <a
              href="/contact"
              className="inline-flex items-center justify-center px-6 py-3 bg-primary text-white rounded-full hover:bg-primary/90 transition-colors"
            >
              Kontakt aufnehmen
            </a>
          </div>
        </section>
      </div>
    </Layout>
  );
};

export default FAQPage;
